import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import eachDayOfInterval from 'date-fns/eachDayOfInterval'
import format from 'date-fns/format'
import isWeekend from 'date-fns/isWeekend'
import stringify from 'safe-stable-stringify'

import { formatDate, parseISODate } from '@avantstay/backoffice-core'

import { storeHolder } from '@new/__global/components/FastTapeChart/stores/TapeChartStore'
import { PeriodSharedProps } from '@new/__global/components/TapeChart/TapeChart.types'

import { AvailableDatesRange, DateKind, ListItem } from './__types'

export const parseDate = (d: string | Date | undefined | null) => {
  if (!d) {
    return 0
  }

  if (d instanceof Date) {
    return d
  }

  return new Date(d.length === 10 ? `${d}T00:00:00` : d)
}

export const shortISODate = (it: Date | number | string | undefined) =>
  it ? format(parseISODate(it), 'yyyy-MM-dd') : ''

export const isSameSet = (
  a: Set<unknown> | null | undefined,
  b?: Set<unknown> | null | undefined,
) => a && b && a.size === b.size && Array.from(a.values()).every(itemFromA => b.has(itemFromA))

export function getColumnOffsets({
  startDate,
  endDate,
  visibleDates,
}: {
  startDate: string
  endDate: string
  visibleDates: Date[]
}) {
  const $firstVisibleDate = visibleDates[0]
  const startDiff =
    Math.round(differenceInCalendarDays(parseISODate(startDate), $firstVisibleDate) + 1) * 2
  const endDiff =
    Math.round(differenceInCalendarDays(parseISODate(endDate), $firstVisibleDate) + 1) * 2

  const minCol = 1
  const maxCol = visibleDates.length * 2 + 1
  return {
    colStart: Math.max(minCol, startDiff),
    colEnd: Math.min(maxCol, endDiff),
    continuing: startDiff < minCol,
    willContinue: endDiff > maxCol,
  }
}

export const getTapeChartDateKind = (d: Date | string | number) => {
  if (isWeekend(parseISODate(d))) {
    return DateKind.weekend
  }

  return DateKind.regular
}

export const minifyXml = (svgCode: string) =>
  svgCode.trim().replace(/(^|>)([\n\s\t]+)(<|$)/g, '$1$3')

export const createRandomClassName = () => `_${Math.random().toString(33).substring(2)}`

const IGNORE_BLOCKS_VALUE = '1'
const DATE_RANGE_SEPARATOR = '_'

export const parseDateRange = (value: string): AvailableDatesRange | undefined => {
  if (!value) {
    return undefined
  }
  const [checkIn, checkOut, noFiltering] = value.split(DATE_RANGE_SEPARATOR)
  return {
    checkIn: formatDate(checkIn),
    checkOut: formatDate(checkOut),
    ignoreBlocks: noFiltering === IGNORE_BLOCKS_VALUE,
  }
}

export const formatDateRange = (value?: AvailableDatesRange): string => {
  if (!value) {
    return ''
  }

  const rangeFields = [value.checkIn, value.checkOut]
  if (value.ignoreBlocks) {
    rangeFields.push(IGNORE_BLOCKS_VALUE)
  }

  return rangeFields.join(DATE_RANGE_SEPARATOR)
}

export interface BlockedDatesByListingId {
  [homeId: string]: string[]
}

export const getBlockedDatesById = (
  periods: PeriodSharedProps[],
  shouldIncludeOneDayLength?: boolean,
): BlockedDatesByListingId => {
  const periodsByHomeId: { [id: string]: PeriodSharedProps[] } = {}
  for (const period of periods) {
    if (periodsByHomeId[period.homeId]) {
      periodsByHomeId[period.homeId].push(period)
    } else {
      periodsByHomeId[period.homeId] = [period]
    }
  }

  /**
   * For blocked periods we need to block out the following dates:
   * - Dates with for the booking, check in and check out dates should be ignored
   * - Dates where there are two events, check in and check out (different bookings)
   * This means the following dates are not blocked
   * - Dates without bookings
   * - Dates with only one event, check in or check out
   */
  return Object.fromEntries(
    Object.entries(periodsByHomeId).map(periodsByHome => {
      const [homeId, blockedPeriods] = periodsByHome

      /**
       * In order to determine which dates have multiple events,
       * We will map out all dates and create an array of any duplicated date
       * Duplicated dates mean that we have both check in and check out on that date.
       */
      const allBlockedPeriods = blockedPeriods.flatMap(period =>
        eachDayOfInterval({
          start: parseISODate(period.startDate),
          end: parseISODate(period.endDate),
        }).map(date => formatDate(date)),
      )

      const seen = new Set<string>()
      const duplicatedBlockedPeriods = new Set<string>()
      for (const item of allBlockedPeriods) {
        if (seen.has(item)) {
          duplicatedBlockedPeriods.add(item)
        } else {
          seen.add(item)
        }
      }

      /**
       * Typically the start and end dates of a blocked period will be removed,
       * Since they are check in and check out dates.
       * However since we can have a date with check in and check out, we need to validate the matching dates,
       * to be able to remove only dates with one or fewer events.
       */
      const updatedBlockedPeriods: string[] = []
      for (const currentPeriod of blockedPeriods) {
        const periodDates = eachDayOfInterval({
          start: parseISODate(currentPeriod.startDate),
          end: parseISODate(currentPeriod.endDate),
        }).map(date => formatDate(date))

        const matchingDates = periodDates.filter(date => duplicatedBlockedPeriods.has(date))
        const filteredDates = periodDates.filter((date: string, index: number) => {
          const numberOfDuplicatedDates = matchingDates.length
          const isDuplicatedDate = duplicatedBlockedPeriods.has(date)

          /**
           * If there are two duplicated dates for the current periodDates
           * We will return all dates as blocked
           * This is because it'll be in-between other bookings, and therefore we can block all of these dates
           */
          if (index === 1 && shouldIncludeOneDayLength) {
            return true
          }

          if (numberOfDuplicatedDates === 2) {
            return true
          }

          /**
           * If the is only one duplicated date in the current periodDates
           * that date will be blocked, since it matches the check in or check out of another booking
           */
          if (numberOfDuplicatedDates === 1 && isDuplicatedDate) {
            return true
          }

          /**
           * A date that is a book end means that it is the check in/out date of a booking
           * All of these dates can be removed, since they are singular events
           * Removing this date will allow for bookings to have a check in/out on this date
           */
          const isDateABookEnd = index === 0 || index === periodDates.length - 1

          return !isDateABookEnd
        })

        updatedBlockedPeriods.push(...filteredDates)
      }

      return [homeId, updatedBlockedPeriods] as const
    }),
  )
}

const listingUnassignedIdPrefix = `unassigned-`
export const getListingUnassignedId = (listingId: string) => {
  return listingUnassignedIdPrefix.concat(listingId)
}
export const isListingUnassignedId = (listingId: string) => {
  return listingId.startsWith(listingUnassignedIdPrefix)
}

type UnassignedBookings = typeof storeHolder.store.apiStore.allUnassignedBookingsByHome

export const getSortedListByCheckInDate = <ListType extends { id: string }>(
  unassignedBookingsListings?: ListItem<ListType>[],
  allUnassignedBookingsByHome?: UnassignedBookings,
) => {
  if (!unassignedBookingsListings || !allUnassignedBookingsByHome) return []

  const earliestCheckInMap = new Map<string, number>()

  const getEarliestCheckIn = (listingId: string) => {
    const cacheKey = getListingUnassignedId(listingId)

    if (earliestCheckInMap.has(cacheKey)) {
      return earliestCheckInMap.get(cacheKey)!
    }

    const bookings = allUnassignedBookingsByHome[cacheKey] || []

    if (!bookings.length) {
      earliestCheckInMap.set(cacheKey, Infinity)
      return Infinity
    }

    const earliestTime = bookings.reduce((earliest, booking) => {
      const checkInTime = new Date(booking.checkInTiming.effectiveTime).getTime()
      return checkInTime < earliest ? checkInTime : earliest
    }, Infinity)

    earliestCheckInMap.set(cacheKey, earliestTime)
    return earliestTime
  }

  return [...unassignedBookingsListings].sort((a, b) => {
    const earliestCheckInA = getEarliestCheckIn(a.id)
    const earliestCheckInB = getEarliestCheckIn(b.id)
    return earliestCheckInA - earliestCheckInB
  })
}

export const memoizeIndexedDB = <
  DataType extends (...args: any[]) => ResultType,
  ResultType extends any,
>(
  fnKey: string,
  fn: DataType,
  ttl: number,
): DataType => {
  const dbName = 'Voyage_MemoizationCache'
  const storeName = 'cache'

  const initializeDB = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName)
        }
      }
    })

  const dbPromise = initializeDB()

  const getFromCache = async (key: string): Promise<any> => {
    const db = await dbPromise
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  const setInCache = async (key: string, value: any): Promise<void> => {
    const db = await dbPromise
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.put(value, key)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  const executeAndCacheFn = async (_fn: DataType, args: unknown[], argsKey: string) => {
    const result = await _fn(...args)
    const cacheValue = {
      [argsKey]: { expiration: Date.now() + ttl, result },
    }

    const existingCache = (await getFromCache(fnKey)) || {}
    const updatedCache = {
      ...existingCache,
      ...cacheValue,
    }

    await setInCache(fnKey, updatedCache)
    return result
  }

  return (async (...args: unknown[]) => {
    const argsKey = stringify(args)
    if (!argsKey) {
      return fn(...args)
    }

    const cache = (await getFromCache(fnKey)) || {}
    const cachedData = cache[argsKey]

    if (!cachedData || cachedData.expiration <= Date.now()) {
      return executeAndCacheFn(fn, args, argsKey)
    }

    return cachedData.result
  }) as DataType
}

export const resolveWithAnimationFrame = <Type,>(thing: Type) =>
  new Promise<Type>(resolve => requestAnimationFrame(() => resolve(thing)))
